import * as Yup from "yup";
import { motion } from "framer-motion";
import {
    ChangePassword,
    ChangePasswordVariables,
} from "_graphql/mutation/__generated__/ChangePassword";
import { useNavigate, useSearch } from "react-location";

import { CHANGE_PASSWORD } from "_graphql/visal/mutations/auth";
import { FC } from "react";
import { LocationGenerics } from "router/location";
import { TextInput } from "components/core";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { Sparkles } from "lucide-react";

const ResetPasswordPage: FC = () => {
    const search = useSearch<LocationGenerics>();
    const navigate = useNavigate();
    const [changePassword, { loading }] = useMutation<
        ChangePassword,
        ChangePasswordVariables
    >(CHANGE_PASSWORD);

    const { handleSubmit, ...form } = useFormik({
        initialValues: {
            password: "",
            confirm_password: "",
        },
        validationSchema: Yup.object().shape({
            password: Yup.string()
                .min(8, "Password should be a minimum of 8")
                .required(),
            confirm_password: Yup.string()
                .test(
                    "passwords-match",
                    "Passwords must match",
                    function (value) {
                        return this.parent.password === value;
                    },
                )
                .required("Password confirmation is required"),
        }),
        onSubmit: async (values) => {
            await changePassword({
                variables: {
                    password: values.password,
                    id: search?.id?.toString() ?? "",
                },
            })
                .then(({ data }) => {
                    if (!data?.changePasswordByEmployee)
                        return toast.error("Could not reset password");
                    return navigate({
                        replace: true,
                        to: `/?from=${"reset_success"}&redirect=${search?.redirect ?? "/"}`,
                    });
                })
                .catch((err) => {
                    toast.error(
                        err?.debugMessage ??
                            err?.message ??
                            "Could not reset password",
                    );
                });
        },
    });

    return (
        <div className="mx-auto w-full max-w-sm lg:w-96 ">
            <div>
                <div className="w-full max-w-lg space-y-6 ">
                    {/* Logo */}
                    <div className="flex justify-center items-center relative text-[#125bed] w-fit mx-auto gap-2">
                        <motion.img
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.1 }}
                            src={require("assets/logo.png")}
                            alt="Visal Re"
                            className="w-32 mx-auto"
                        />
                        <span className="text-2xl font-bold">Lite</span>
                    </div>

                    {/* AI Badge */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="flex items-center justify-center gap-2 text-sm font-medium text-[#2e5cb8] mb-2">
                        <Sparkles className="w-4 h-4" />
                        <span className=" tracking-wide">
                            AI Powered Reinsurance Dashboard
                        </span>
                    </motion.div>
                </div>
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                    Reset Password
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                    Provide new password to keep your access secure
                </p>
            </div>

            <div className="mt-8">
                <div className="mt-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <TextInput
                                id="password"
                                label="Password"
                                type="password"
                                placeholder="e.g.  **************"
                                {...form}
                            />
                        </div>

                        <div className="space-y-1">
                            <TextInput
                                id="confirm_password"
                                label="Confirm Password"
                                type="password"
                                placeholder="e.g.  **************"
                                {...form}
                            />
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                {loading ? "Resetting..." : "Reset Password"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
